/**
 * ScrollProgressBar.tsx
 * 
 * A customizable scroll progress bar with multiple visual styles:
 * - Normal: Clean gradient bar
 * - Lightning: Pulsating electrical effect with glow (constrained within progress)
 * - Binary: Floating 0/1 data particles that rise and dissolve (CyberClicker-inspired)
 * - Fiber: Fiber optic light pulses traveling across the bar (constrained within progress)
 * 
 * KEY DESIGN DECISIONS:
 * - Height reduced by ~40% from h-3 to h-[7px] for subtlety
 * - 80% opacity to be slightly transparent
 * - Lightning spark uses relative positioning + overflow:hidden so it never exceeds the bar
 * - Binary effect spawns floating "01" particles that rise and vanish like data transmission
 * - Settings are persisted via localStorage in App.tsx; defaults are Neon Emerald + Lightning
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, useScroll, useSpring, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import type { ProgressBarStyle, ProgressBarColor } from './SettingsPanel';
import { COLOR_OPTIONS } from './SettingsPanel';

// -- Binary particle type for the floating "data transmission" effect --
interface BinaryParticle {
    id: number;
    x: number;       // Horizontal position (percentage along bar)
    text: string;     // Data fragment to display
}

// Data fragments that look like real transmitted data
const DATA_FRAGMENTS = [
    '01', '10', '0x', '1F', 'FF', '00', '11',
    'ACK', 'SYN', 'TCP', 'UDP', 'GET', 'PUT',
    '>>>', '<<<', '0xF', 'A5', 'OK', 'RX', 'TX',
];

interface ScrollProgressBarProps {
    style: ProgressBarStyle;
    color: ProgressBarColor;
}

export function ScrollProgressBar({ style, color }: ScrollProgressBarProps) {
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    const colorConfig = COLOR_OPTIONS[color];

    // -- Binary particle state --
    const [particles, setParticles] = useState<BinaryParticle[]>([]);
    const particleIdRef = useRef(0);
    const scrollProgressRef = useRef(0);

    // Track current scroll progress for particle spawning
    useMotionValueEvent(scaleX, "change", (latest) => {
        scrollProgressRef.current = latest;
    });

    // Spawn particles periodically when binary style is active and user is scrolling
    const spawnParticle = useCallback(() => {
        const progress = scrollProgressRef.current;
        if (progress <= 0.01) return; // Don't spawn at 0%

        const id = particleIdRef.current++;
        const x = Math.random() * progress * 100; // Only spawn within filled area
        const text = DATA_FRAGMENTS[Math.floor(Math.random() * DATA_FRAGMENTS.length)];

        setParticles(prev => [...prev, { id, x, text }]);

        // Auto-remove after animation completes (800ms)
        setTimeout(() => {
            setParticles(prev => prev.filter(p => p.id !== id));
        }, 800);
    }, []);

    // Particle spawning interval for binary style
    useEffect(() => {
        if (style !== 'binary') {
            setParticles([]);
            return;
        }

        const interval = setInterval(spawnParticle, 200);
        return () => clearInterval(interval);
    }, [style, spawnParticle]);

    return (
        <div className="fixed top-0 left-0 right-0 h-[7px] z-[100] overflow-visible" style={{ opacity: 0.8 }}>
            {/* Base progress bar - always present */}
            <motion.div
                className="absolute inset-0 origin-left"
                style={{
                    scaleX,
                    background: colorConfig.gradient,
                }}
            />

            {/* Style-specific overlay effects */}
            {style === 'lightning' && (
                <motion.div
                    className="absolute inset-0 origin-left overflow-hidden"
                    style={{ scaleX }}
                >
                    {/* Lightning glow effect - constrained WITHIN progress */}
                    <div
                        className="absolute inset-0 animate-pulse"
                        style={{
                            boxShadow: `0 0 10px 2px ${colorConfig.glow}, 0 0 20px 4px ${colorConfig.glow}`,
                            background: colorConfig.gradient,
                        }}
                    />
                    {/* Traveling spark — overflow:hidden on parent keeps it inside */}
                    <div
                        className="absolute top-0 right-0 w-8 h-full"
                        style={{
                            background: `linear-gradient(90deg, transparent, white, transparent)`,
                            animation: 'lightning-spark 1.5s ease-in-out infinite',
                        }}
                    />
                </motion.div>
            )}

            {style === 'binary' && (
                <>
                    {/* Base bar for binary style */}
                    <motion.div
                        className="absolute inset-0 origin-left overflow-hidden"
                        style={{ scaleX }}
                    >
                        <div
                            className="absolute inset-0"
                            style={{
                                background: colorConfig.gradient,
                                boxShadow: `0 0 8px 1px ${colorConfig.glow}`,
                            }}
                        />
                    </motion.div>

                    {/* Floating data particles — rise upward and dissolve */}
                    <AnimatePresence>
                        {particles.map(particle => (
                            <motion.span
                                key={particle.id}
                                initial={{ opacity: 1, y: 0, scale: 0.5 }}
                                animate={{ opacity: 0, y: -30, scale: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.8, ease: 'easeOut' }}
                                className="absolute font-mono pointer-events-none font-bold"
                                style={{
                                    left: `${particle.x}%`,
                                    top: '0',
                                    fontSize: '9px',
                                    color: colorConfig.glow,
                                    textShadow: `0 0 4px ${colorConfig.glow}`,
                                    letterSpacing: '0.05em',
                                }}
                            >
                                {particle.text}
                            </motion.span>
                        ))}
                    </AnimatePresence>
                </>
            )}

            {style === 'fiber' && (
                <motion.div
                    className="absolute inset-0 origin-left overflow-hidden"
                    style={{ scaleX }}
                >
                    <div className="absolute inset-0" style={{ background: colorConfig.gradient }} />
                    {/* Fiber optic light pulses — contained within the bar via overflow:hidden */}
                    <div
                        className="absolute top-0 w-16 h-full"
                        style={{
                            background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)`,
                            animation: 'fiber-pulse 2s ease-in-out infinite',
                        }}
                    />
                    <div
                        className="absolute top-0 w-8 h-full"
                        style={{
                            background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)`,
                            animation: 'fiber-pulse 2s ease-in-out infinite 0.7s',
                        }}
                    />
                </motion.div>
            )}
        </div>

    );
}
